import { obstaclesNear, pushOutOfObstacles, type Obstacle } from './obstacles';
import { nextRandom } from './rng';
import { effect } from './effects';
import { refreshModifiers } from './progression';
import { applyModifiers } from './stats';
import type { Drop, DropKind, Enemy, GameState } from './types';
import { pullLoot, scatterLoot } from './physics';

/**
 * Łupy na wyprawie: monety i mikstury z potworów, skrzynki pojawiające się na mapie,
 * przedmioty wyprawy (pierścienie, amulety, talizmany — działają tylko do końca wyprawy)
 * i sprzęt główny od bossów, który po wyprawie trafia do ekwipunku.
 */

const blockers: Obstacle[] = [];
const obstacleCount = (s: GameState) => s.content.zones[s.zoneId]?.obstacles ?? 0;

export function addDrop(s: GameState, kind: DropKind, x: number, y: number, value: number, item?: { itemId: string; rarity: string }): void {
  // Lekki rozrzut, żeby kilka łupów z jednego miejsca nie leżało na sobie.
  const a = nextRandom(s) * Math.PI * 2;
  const r = 6 + nextRandom(s) * 22;
  const drop: Drop = { id: s.nextId++, kind, x: x + Math.cos(a) * r, y: y + Math.sin(a) * r, value, magnet: false };
  if (item) {
    drop.itemId = item.itemId;
    drop.rarity = item.rarity;
  }
  s.drops.push(drop);
}

/** Rzadkość przedmiotu z wyprawy: zwykłe wagi rzadkości; `luck` przesuwa los w stronę lepszych. */
function rollRarity(s: GameState, luck: number): string {
  const order = s.content.rarityOrder;
  const weights = order.map((id, i) => s.content.rarities[id]!.weight * (1 + luck * i));
  const total = weights.reduce((a, b) => a + b, 0);
  let roll = nextRandom(s) * total;
  for (let i = 0; i < order.length; i++) {
    roll -= weights[i]!;
    if (roll <= 0) return order[i]!;
  }
  return order[order.length - 1]!;
}

/** Losowy sprzęt dla klasy bohatera. */
export function rollItem(s: GameState, luck: number): { itemId: string; rarity: string } | null {
  const items = Object.values(s.content.items).filter((i) => i.classes.includes(s.classId));
  if (items.length === 0) return null;
  const def = items[Math.floor(nextRandom(s) * items.length)]!;
  return { itemId: def.id, rarity: rollRarity(s, luck) };
}

/**
 * Losowy przedmiot wyprawy: rzadkość jak przy sprzęcie (szczęście z drzewka i przedmiotów ją poprawia),
 * a gdy w tej rzadkości nic nie ma — najbliższa niższa.
 */
export function rollRelic(s: GameState, luck: number): { relicId: string; rarity: string } | null {
  const all = Object.values(s.content.relics);
  if (all.length === 0) return null;
  const order = s.content.rarityOrder;
  for (let i = order.indexOf(rollRarity(s, luck + effect(s, 'luck'))); i >= 0; i--) {
    const pool = all.filter((r) => r.rarity === order[i]);
    if (pool.length) return { relicId: pool[Math.floor(nextRandom(s) * pool.length)]!.id, rarity: order[i]! };
  }
  const any = all[Math.floor(nextRandom(s) * all.length)]!;
  return { relicId: any.id, rarity: any.rarity };
}

/** Szansa na przedmiot wyprawy po premii z drzewka (Szczęśliwa Ręka, Skarbiec). */
const relicChance = (s: GameState, base: number) => Math.min(1, base * applyModifiers(1, 'relicFind', s.modifiers));

function dropRelic(s: GameState, x: number, y: number, luck: number): void {
  const relic = rollRelic(s, luck);
  if (!relic) return;
  addDrop(s, 'relic', x, y, 1);
  const d = s.drops[s.drops.length - 1]!;
  d.relicId = relic.relicId;
  d.rarity = relic.rarity;
}

const rewardMul = (s: GameState) => s.content.zones[s.zoneId]?.rewardMultiplier ?? 1;

/** Co wypada z pokonanego wroga. Silniejsi (elitarni) częściej zostawiają przedmiot wyprawy. */
export function dropFromEnemy(s: GameState, e: Enemy): void {
  const cfg = s.content.game.loot;
  const elite = e.radius >= cfg.eliteRadius;
  if (nextRandom(s) < (elite ? 1 : cfg.goldDropChance)) {
    const value = Math.max(1, Math.round((1 + nextRandom(s) * 2) * rewardMul(s) * (elite ? 4 : 1)));
    addDrop(s, 'gold', e.x, e.y, value);
  }
  if (nextRandom(s) < relicChance(s, elite ? cfg.eliteItemChance : cfg.itemDropChance)) dropRelic(s, e.x, e.y, elite ? 0.6 : 0);
  if (!elite && nextRandom(s) < cfg.potionDropChance) addDrop(s, 'potion', e.x, e.y, 1);
}

/** Boss: deszcz monet, pewny przedmiot (zwykle lepszej rzadkości) i skrzynia w miejscu, gdzie padł. */
export function dropFromBoss(s: GameState, e: Enemy): void {
  for (let i = 0; i < 12; i++) addDrop(s, 'gold', e.x, e.y, Math.max(1, Math.round((3 + nextRandom(s) * 4) * rewardMul(s))));
  const item = rollItem(s, 2.5);
  if (item) addDrop(s, 'item', e.x, e.y, 1, item);
  s.chests.push({ id: s.nextId++, x: e.x + 30, y: e.y });
}

/** Koniec wyprawy zwycięstwem: wszystko, co leży na ziemi (i w skrzyniach), trafia do bohatera. */
export function collectAll(s: GameState): void {
  for (const c of s.chests) openChest(s, c.x, c.y);
  s.chests = [];
  for (const d of s.drops) if (d.kind !== 'potion') pickUp(s, d);
  s.drops = [];
}

/** Skrzynki pojawiają się co jakiś czas w pewnej odległości od bohatera. Za daleko zostawione znikają. */
export function updateChests(s: GameState, dt: number): void {
  const cfg = s.content.game.loot;
  const p = s.player;
  s.chests = s.chests.filter((c) => Math.hypot(c.x - p.x, c.y - p.y) < cfg.chestDespawnDistance);
  s.chestTimer -= dt;
  if (s.chestTimer <= 0) {
    s.chestTimer = cfg.chestInterval / Math.max(0.2, applyModifiers(1, 'chestRate', s.modifiers));
    if (s.chests.length < cfg.maxChests) {
      const angle = nextRandom(s) * Math.PI * 2;
      const dist = cfg.chestDistance[0] + nextRandom(s) * (cfg.chestDistance[1] - cfg.chestDistance[0]);
      const chest = { id: s.nextId++, x: p.x + Math.cos(angle) * dist, y: p.y + Math.sin(angle) * dist };
      // Nie w środku drzewa ani grobu.
      pushOutOfObstacles(chest, 20, obstaclesNear(s.seed, obstacleCount(s), chest.x, chest.y, 20, blockers));
      s.chests.push(chest);
    }
  }
  const reach = s.content.game.playerRadius + 18;
  for (const c of s.chests) {
    if (Math.hypot(c.x - p.x, c.y - p.y) > reach) continue;
    openChest(s, c.x, c.y);
    c.id = -1;
  }
  s.chests = s.chests.filter((c) => c.id >= 0);
}

function openChest(s: GameState, x: number, y: number): void {
  const cfg = s.content.game.loot;
  s.events.push({ type: 'chestOpened', x, y });
  const coins = cfg.chestCoins[0] + Math.floor(nextRandom(s) * (cfg.chestCoins[1] - cfg.chestCoins[0] + 1));
  for (let i = 0; i < coins; i++) addDrop(s, 'gold', x, y, Math.max(1, Math.round((2 + nextRandom(s) * 3) * rewardMul(s))));
  if (nextRandom(s) < relicChance(s, cfg.chestItemChance)) dropRelic(s, x, y, 0.8);
  if (nextRandom(s) < cfg.chestPotionChance) addDrop(s, 'potion', x, y, 1);
}

/** Zbieranie łupów: przyciągane jak kryształy doświadczenia, gdy bohater jest w zasięgu zbierania. */
export function updateDrops(s: GameState, dt: number): void {
  const p = s.player;
  const collect = s.content.game.playerRadius + 8;
  for (const d of s.drops) {
    scatterLoot(s, d, dt);
    const dist = Math.hypot(p.x - d.x, p.y - d.y);
    if (dist <= collect) {
      pickUp(s, d);
      d.value = 0;
      continue;
    }
    // Mikstura przyciąga się tylko, gdy bohater jest ranny — inaczej czeka na później.
    const wanted = d.kind !== 'potion' || p.hp < p.stats.maxHealth;
    if (wanted && dist <= p.stats.pickupRadius) d.magnet = true;
    if (d.magnet && wanted) pullLoot(s, d, dt);
  }
  s.drops = s.drops.filter((d) => d.value > 0);
}

function pickUp(s: GameState, d: Drop): void {
  const p = s.player;
  if (d.kind === 'gold') {
    // Drzewko rozwoju (Chciwość) mnoży złoto; zaokrąglamy w górę, żeby każda moneta coś dawała.
    s.gold += Math.ceil(d.value * applyModifiers(1, 'goldGain', s.modifiers) - 1e-9);
  } else if (d.kind === 'potion') {
    p.hp = Math.min(p.stats.maxHealth, p.hp + p.stats.maxHealth * s.content.game.loot.potionHeal);
  } else if (d.kind === 'relic' && d.relicId) {
    // Działa od razu: modyfikatory przedmiotu wchodzą do statystyk do końca wyprawy.
    s.relics.push({ relicId: d.relicId });
    refreshModifiers(s);
  } else if (d.itemId && d.rarity) {
    s.loot.push({ itemId: d.itemId, rarity: d.rarity });
  }
  s.events.push({ type: 'pickup', kind: d.kind, value: d.value, x: d.x, y: d.y, itemId: d.itemId, relicId: d.relicId, rarity: d.rarity });
}
